import { Injectable } from '@angular/core';
import { Observable, of as observableOf } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { ApplicationHttpClient } from '../components/shared/http/application-http-client';
import { Routes } from '../config/routes';

interface IntercomIdentityResponse {
    appId: string | null;
    intercomUserJwt: string | null;
    expiresAt: string | null;
}

export interface IntercomIdentity {
    appId: string | null;
    jwt: string | null;
}

@Injectable({
    providedIn: 'root',
})
export class IntercomIdentityService {
    private cachedIdentity: IntercomIdentity | null = null;
    private expiresAt: Date | null = null;

    constructor(private _http: ApplicationHttpClient) {}

    /**
     * Get Intercom identity (appId + JWT), fetching from API if needed.
     * Uses caching to avoid unnecessary API calls.
     */
    getIdentity(): Observable<IntercomIdentity> {
        // Return cached identity if JWT still valid (with 5-minute buffer)
        if (this.cachedIdentity && this.expiresAt) {
            const bufferTime = 5 * 60 * 1000; // 5 minutes
            if (new Date().getTime() < this.expiresAt.getTime() - bufferTime) {
                return observableOf(this.cachedIdentity);
            }
        }

        // Fetch from API
        return this._http.get<IntercomIdentityResponse>(
            Routes.MakeLegFiCoreUrl('/intercom/identity')
        ).pipe(
            tap(response => {
                const identity: IntercomIdentity = {
                    appId: response.appId,
                    jwt: response.intercomUserJwt,
                };
                this.cachedIdentity = identity;

                if (response.expiresAt) {
                    this.expiresAt = new Date(response.expiresAt);
                }
            }),
            map(response => ({
                appId: response.appId,
                jwt: response.intercomUserJwt,
            })),
            catchError(error => {
                console.error('Failed to fetch Intercom identity:', error);
                return observableOf({ appId: null, jwt: null });
            })
        );
    }

    /**
     * @deprecated Use getIdentity() instead. Kept for safety during transition.
     */
    getIdentityJwt(): Observable<string | null> {
        return this.getIdentity().pipe(
            map(identity => identity.jwt)
        );
    }

    /**
     * Clear cached identity (call on logout).
     */
    clearCache(): void {
        this.cachedIdentity = null;
        this.expiresAt = null;
    }
}
